import { formatMinutesToHoursString } from "@components/inputs/date/utils";
import { getDaysUnit, getHoursUnit, getWeeksUnit, IFieldDefFn, IGetValueArgs, not } from "@components/smart/FieldInfo";
import {
    getCommonFilterDefs,
    getCommonTableColumnsDefs,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import {
    EntitySetName,
    EntityTypeName,
    IPrWorkingPatternDayEntity,
    IPrWorkingPatternDayIntervalEntity,
    IPrWorkingPatternEntity,
    PrWorkingPatternDayEntity,
    PrWorkingPatternDayIntervalEntity,
    PrWorkingPatternEntity
} from "@odata/GeneratedEntityTypes";
import { PrWorkingPatternIntervalTypeCode, PrWorkingPatternTypeCode } from "@odata/GeneratedEnums";
import { WorkingPatternText } from "@pages/payroll/workingPatterns/WorkingPattern.styles";
import {
    BaseWorkingHoursPerWeekDef,
    getHoursDiffFormatted
} from "@pages/payroll/workingPatterns/WorkingPatterns.utils";
import { isDefined, roundToDecimalPlaces } from "@utils/general";
import Big from "big.js";
import i18next from "i18next";
import React from "react";
import { ValidationError } from "yup";

import { DASH_CHARACTER } from "../../../constants";
import { BasicInputSizes, CacheStrategy, FieldType, LabelStatus, Sort, TextAlign, ValidatorType } from "../../../enums";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import { getUtcDate, getUtcDayjs } from "../../../types/Date";
import NumberType from "../../../types/Number";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import SmartCalendarWrapper from "./SmartCalendarWrapper";
import WorkDayIntervalsFastEntry from "./WorkDayIntervalsFastEntry";
import WorkingPatternsFormView, { IWorkingPatternCustomData } from "./WorkingPatternsFormView";

export const calendarPath = BindingContext.localContext("Calendar");
export const rotationLengthPath = BindingContext.localContext("RotationLength");
export const dateStartPath = BindingContext.localContext("DateStart");
export const workDayIntervalsFastEntryPath = BindingContext.localContext("WorkDayIntervalsFastEntry");
export const workingPatternFullTimeValuePath = BindingContext.localContext("WorkingPatternFullTimeValuePath");
export const timeDiffPath = BindingContext.localContext("TimeDiff");

export const SHIFT_GROUP_ID = "shifts";

const isRotation = (args: IGetValueArgs): boolean => {
    return args.storage.getValueByPath(PrWorkingPatternEntity.TypeCode) === PrWorkingPatternTypeCode.Rotation;
};

const isUneven = (args: IGetValueArgs): boolean => {
    return args.storage.getValueByPath(PrWorkingPatternEntity.TypeCode) === PrWorkingPatternTypeCode.Uneven;
};

const MAX_WORKING_HOURS_PER_WEEK = 40;

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrWorkingPattern}Table`,
        title: i18next.t("WorkingPatterns:Title"),
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrWorkingPatternEntity.Name,
                PrWorkingPatternEntity.Type
            ],
            filterDefinition: {
                ...getCommonFilterDefs(),
                Name: {},
                ...withDisplayName(PrWorkingPatternEntity.Type)
            },
            isValueHelp: true
        }],
        // According to https://solitea-cz.atlassian.net/browse/DEV-43663 hide rotation pattern
        filter: `TypeCode ne '${PrWorkingPatternTypeCode.Rotation}'`,
        columns: [
            PrWorkingPatternEntity.Name,
            PrWorkingPatternEntity.Type
        ],
        columnDefinition: {
            ...getCommonTableColumnsDefs(),
            Name: {},
            ...withDisplayName(PrWorkingPatternEntity.Type)
        },
        initialSortBy: [{ id: PrWorkingPatternEntity.Name, sort: Sort.Desc }]
    };

    const form: IFormDef = {
        id: `${EntityTypeName.PrWorkingPattern}Form`,
        translationFiles: getDefinitions.translationFiles,

        getItemBreadCrumbText: (storage: Model) =>
                getItemBreadCrumbsText(storage, i18next.t("WorkingPatterns:FormTitle"), storage.data.entity?.Name),
        isDeletable: true,
        formControl: WorkingPatternsFormView,
        additionalProperties: [{
            id: PrWorkingPatternEntity.DateLastModified
        },
            {
                id: PrWorkingPatternEntity.Days,
                additionalProperties: [{
                    id: PrWorkingPatternDayEntity.WorkingHours
                }, {
                    id: PrWorkingPatternDayEntity.Date
                }, {
                    id: PrWorkingPatternDayEntity.Intervals,
                    additionalProperties: [{
                        id: PrWorkingPatternDayIntervalEntity.Type
                    }, {
                        id: PrWorkingPatternDayIntervalEntity.TimeStart
                    }, {
                        id: PrWorkingPatternDayIntervalEntity.TimeEnd
                    }, {
                        id: PrWorkingPatternDayIntervalEntity.IsStartNextDay
                    }, {
                        id: PrWorkingPatternDayIntervalEntity.IsEndNextDay
                    }]
                }]
            }],
        fieldDefinition: {
            Name: {
                width: BasicInputSizes.L
            },
            TypeCode: {
                type: FieldType.SegmentedButton,
                defaultValue: PrWorkingPatternTypeCode.Weekly,
                labelStatus: LabelStatus.Hidden,
                fieldSettings: {
                    items: [
                        {
                            id: PrWorkingPatternTypeCode.Weekly,
                            label: i18next.t("WorkingPatterns:Type.Weekly")
                        }, /*
                        According to https://solitea-cz.atlassian.net/browse/DEV-43663 hide rotation pattern
                        {
                            id: PrWorkingPatternTypeCode.Rotation,
                            label: i18next.t("WorkingPatterns:Type.Rotation")
                        }, */{
                            id: PrWorkingPatternTypeCode.Uneven,
                            label: i18next.t("WorkingPatterns:Type.Uneven")
                        }
                    ]
                }
            },
            IsDifferentOddAndEvenWeek: {
                type: FieldType.Checkbox,
                labelStatus: LabelStatus.Removed,
                isVisible: args => {
                    return args.storage.getValueByPath(PrWorkingPatternEntity.TypeCode) === PrWorkingPatternTypeCode.Weekly;
                }
            },
            DefaultDayStart: {
                type: FieldType.Time,
                width: BasicInputSizes.XS,
                label: i18next.t("WorkingPatterns:DefaultTimeStartLabel"),
                tooltip: i18next.t("WorkingPatterns:DefaultTimeStart"),
                defaultValue: () => {
                    return getUtcDayjs().startOf("day").add(8, "hours").toDate();
                },
                fieldSettings: {
                    showSteppers: false
                }
            },
            [calendarPath]: {
                type: FieldType.Custom,
                isVisible: not(isUneven),
                render: (args: IFieldDefFn) => {
                    const storage = args.storage as FormStorage<IPrWorkingPatternEntity, IWorkingPatternCustomData>;
                    const isReadOnly = !!storage.getBackendDisabledFieldMetadata(
                        storage.data.bindingContext.navigate(PrWorkingPatternEntity.Days)
                    );
                    return <SmartCalendarWrapper storage={storage} isReadOnly={isReadOnly}/>;
                }
            },
            [workingPatternFullTimeValuePath]: {
                type: FieldType.Custom,
                render: (args: IFieldDefFn) => {
                    const storage = args.storage as FormStorage<IPrWorkingPatternEntity, IWorkingPatternCustomData>;
                    const _isUneven = isUneven({ storage });
                    const value = storage.getValueByPath(PrWorkingPatternEntity.HoursPerWeekFullTime);
                    const averageMin = storage.getValueByPath(PrWorkingPatternEntity.WorkingMinutesWeekly);
                    const averageHours = averageMin ? new Big(averageMin).div(60).toNumber() : null;
                    const average = averageMin ? formatMinutesToHoursString(averageMin) : null;
                    const percent = (value && average) ? NumberType.format(roundToDecimalPlaces(2, averageHours * 100 / value)) : null;
                    return (
                            <WorkingPatternText>
                                {!_isUneven && (<>
                                    {i18next.t("WorkingPatterns:AverageWeekFund")} <strong>{average ?? DASH_CHARACTER}</strong><br/>
                                </>)}
                                {i18next.t("WorkingPatterns:WorkingHours")} <strong>{percent ?? DASH_CHARACTER} %</strong>
                            </WorkingPatternText>
                    );
                }
            },
            [workDayIntervalsFastEntryPath]: {
                type: FieldType.Custom,
                isVisible: (args: IGetValueArgs) => {
                    const storage = args.storage as FormStorage<IPrWorkingPatternEntity, IWorkingPatternCustomData>;
                    return !!storage.getCustomData().selectedDay;
                },
                render: (args: IFieldDefFn) => {
                    const storage = args.storage as FormStorage<IPrWorkingPatternEntity, IWorkingPatternCustomData>;
                    const isReadOnly = !!storage.getBackendDisabledFieldMetadata(
                        storage.data.bindingContext.navigate(PrWorkingPatternEntity.Days)
                    );
                    return <WorkDayIntervalsFastEntry storage={storage} isReadOnly={isReadOnly}/>;
                }
            },
            [dateStartPath]: {
                type: FieldType.Date,
                label: i18next.t("WorkingPatterns:RotationStart"),
                isRequired: true,
                width: BasicInputSizes.M,
                isVisible: isRotation,
                defaultValue: () => getUtcDate()
            },
            [rotationLengthPath]: {
                type: FieldType.NumberInput,
                label: i18next.t("WorkingPatterns:RotationLength"),
                isRequired: true,
                width: BasicInputSizes.S,
                isVisible: isRotation,
                validator: {
                    type: ValidatorType.Integer,
                    settings: {
                        min: 1,
                        max: 365
                    }
                },
                fieldSettings: {
                    unit: getDaysUnit
                }
            },
            [createPath(PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.Intervals, PrWorkingPatternDayIntervalEntity.Type)]: {
                type: FieldType.ComboBox,
                cacheStrategy: CacheStrategy.EndOfTime,
                fieldSettings: {
                    displayName: "Name"
                },
                customizationData: {
                    useForCustomization: false
                },
                defaultValue: (args: IGetValueArgs) => {
                    const dayBc = args.bindingContext.getParentCollection().getParent();
                    const day = args.storage.getValue(dayBc) as IPrWorkingPatternDayEntity;
                    const lastInterval = day.Intervals?.at(-1);
                    return lastInterval?.Type?.Code === PrWorkingPatternIntervalTypeCode.Work ? PrWorkingPatternIntervalTypeCode.MealBreak : PrWorkingPatternIntervalTypeCode.Work;
                }
            },
            [createPath(PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.Intervals, PrWorkingPatternDayIntervalEntity.TimeStart)]: {
                type: FieldType.Time,
                width: BasicInputSizes.XS,
                fieldSettings: {
                    showSteppers: false
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            [createPath(PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.Intervals, PrWorkingPatternDayIntervalEntity.TimeEnd)]: {
                type: FieldType.Time,
                width: BasicInputSizes.XS,
                fieldSettings: {
                    showSteppers: false
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            [createPath(PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.Intervals, timeDiffPath)]: {
                isReadOnly: true,
                width: BasicInputSizes.XS,
                label: i18next.t("WorkingPatterns:Length"),
                fieldSettings: {
                    unit: i18next.t("Components:Calendar.HourPlaceholder")
                },
                textAlign: TextAlign.Right,
                formatter: (val, args) => {
                    const intervalEntity = args.storage.getValue(args.bindingContext.getParent()) as IPrWorkingPatternDayIntervalEntity;
                    return getHoursDiffFormatted(intervalEntity?.TimeStart, intervalEntity?.TimeEnd, intervalEntity?.IsStartNextDay !== intervalEntity?.IsEndNextDay);
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            BalancingPeriodWeeks: {
                label: i18next.t("WorkingPatterns:BalancingPeriodWeeks"),
                isRequired: isUneven,
                isVisible: isUneven,
                fieldSettings: {
                    unit: getWeeksUnit
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        max: 52
                    }
                },
            },
            [PrWorkingPatternEntity.DateBalancingPeriodStart]: {
                type: FieldType.Date,
                label: i18next.t("WorkingPatterns:DateBalancingPeriodStart"),
                isRequired: isUneven,
                isVisible: isUneven,
                width: BasicInputSizes.M
            },
            WorkingMinutesWeekly: {
                formatter: (val: TValue) => {
                    if (!isDefined(val) || isNaN(Number(val))) {
                        return val?.toString();
                    }
                    return new Big(Number(val)).div(60).round(2).toNumber();
                },
                parser: (val: string) => {
                    const number = NumberType.parse(val);
                    if (isNaN(number) || number === null) {
                        return number;
                    }
                    return new Big(number).mul(60).round().toNumber();
                },
                fieldSettings: {
                    unit: getHoursUnit
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        customValidator(value, args) {
                            const maxHoursPerWeek = Math.min(args.storage.getValueByPath(PrWorkingPatternEntity.HoursPerWeekFullTime), MAX_WORKING_HOURS_PER_WEEK);
                            if (isDefined(value) && Number(value) > Number(maxHoursPerWeek) * 60) {
                                return new ValidationError(i18next.t("WorkingPatterns:ErrorWeeklyHoursExceedFullTime").toString(), false, args.bindingContext.getPath(true));
                            }
                            return true;
                        }
                    },
                },
                affectedFields: [{ id: workingPatternFullTimeValuePath }]
            },
            [PrWorkingPatternEntity.HoursPerWeekFullTime]: {
                ...BaseWorkingHoursPerWeekDef,
                width: BasicInputSizes.M,
                label: i18next.t("WorkingPatterns:Form.FullTimeHoursPerWeek"),
                affectedFields: [{ id: PrWorkingPatternEntity.WorkingMinutesWeekly, revalidate: true }]
            },
            [PrWorkingPatternEntity.IsIncludingHolidays]: {
                type: FieldType.Checkbox,
                labelStatus: LabelStatus.Removed,
                defaultValue: false,
                label: i18next.t("WorkingPatterns:IsIncludingHolidays"),
                isVisible: not(isUneven)
            }
        },
        groups: [
            {
                id: "main",
                rows: [
                    [{ id: PrWorkingPatternEntity.Name }, { id: PrWorkingPatternEntity.DefaultDayStart }, { id: PrWorkingPatternEntity.WorkingMinutesWeekly }, { id: PrWorkingPatternEntity.HoursPerWeekFullTime }],
                    [{ id: PrWorkingPatternEntity.TypeCode }]
                ]
            }, {
                id: SHIFT_GROUP_ID,
                title: i18next.t("WorkingPatterns:Shifts"),
                customizationData: {
                    isLocked: true
                },
                rows: [
                    [{ id: PrWorkingPatternEntity.IsDifferentOddAndEvenWeek }, { id: rotationLengthPath }, { id: dateStartPath }],
                    [{ id: calendarPath }],
                    [{ id: PrWorkingPatternEntity.IsIncludingHolidays }],
                    [{ id: PrWorkingPatternEntity.BalancingPeriodWeeks }, { id: PrWorkingPatternEntity.DateBalancingPeriodStart }],
                    [{ id: workingPatternFullTimeValuePath }],
                    [{ id: workDayIntervalsFastEntryPath }],
                ]
            }
        ],
        querySettings: {
            "Days/Intervals": {
                sort: [{ id: PrWorkingPatternDayIntervalEntity.Order, sort: Sort.Asc }]
            }
        }
    };

    return {
        entitySet: EntitySetName.PrWorkingPatterns,
        table,
        form
    };
};

getDefinitions.translationFiles = ["WorkingPatterns", "Common"];
setDefByEntityType(EntityTypeName.PrWorkingPattern, getDefinitions);